<template>
	<div
		class="configurator__holder"
		:class="{'configurator__holder--full' : configuratorType.isConfiguration }"
		v-if="!configuratorDataLoaded">
		<div class="configurator__loader">
			<div id="loader" />
		</div>
	</div>
	<div
		class="configurator__holder"
		:class="{'configurator__holder--full' : configuratorType.isConfiguration }"
		v-else-if="configuratorDataLoaded">
		<div class="scroll-hook" />
		<div class="grid grid--gutters grid--m-full flex-space--between">
			<configurator />
		</div>
		<modal
			button-style="none" modal-style="styled"
			:open="modalOpened" :title="modalContent.title">
			<div v-html="modalContent.content" />
		</modal>
	</div>
</template>

<script>
import { EventBus } from "@/store/index.js";
import configurator from "@/components/configurator";
import modal from "@/components/element/modal";
import { Events, pushEvent } from "@/helpers/tracking.js";

export default {
	name: "App",
	components: {
		configurator,
		modal
	},
	data () {
		return {
			modalOpened: false,
			modalContent: {
				title: "",
				content: ``
			}
		};
	},
	computed: {
		configuratorType () {
			return this.$store.getters.getConfiguratorType;
		},
		configuratorDataLoaded () {
			return this.$store.state.isLoaded;
		}
	},
	mounted () {
		EventBus.$on("configuratorModal", object => {
			this.modalContent = object || { title: "", content: "" };

			this.modalOpened = false;
			setTimeout(
				function () {
					this.modalOpened = true;
				}.bind(this),
				100
			);
		});
	},
	watch: {
		configuratorDataLoaded (newVal) {
			if (newVal) {
				document.body.classList.add("configurator--loaded");

				let configuratorType = "initial";
				if (this.configuratorType.isConfiguration) {
					configuratorType = "configuration";
				} else if (this.configuratorType.isUploader) {
					configuratorType = "uploader";
				} else if (this.configuratorType.isCalculate) {
					configuratorType = "calculate";
				}

				pushEvent(Events.INIT, {
					configuratorType: configuratorType,
					hasMultipleProducts: this.$store.state.configuratorData.hasMultipleProducts
				});
			}
		}
	}
};
</script>

<style lang="scss">
@import "./assets/scss/theme-defaults";
@import "./assets/scss/configurator-step";

.button--opaque {
  opacity: 0.3 !important;
  filter: grayscale(0.7) !important;
}

.configurator__holder {
  display: block;
  position: relative;
  width: 100%;
  min-height: 250px;
  overflow: visible;

  small {
    font-size: 70%;
    font-weight: 300;
  }
}

.configurator__loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  vertical-align: middle;

  &--overlay {
    background: rgba(255, 255, 255, 0.7);
  }
}

#loader {
  display: block;
  position: relative;
  left: 50%;
  top: 50%;
  width: 100px;
  height: 100px;
  margin: -50px 0 0 -50px;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: #3498db;
  animation: spin 1.8s linear infinite; /* Chrome, Firefox 16+, IE 10+, Opera */
}

#loader:before {
  content: "";
  position: absolute;
  top: 5px;
  left: 5px;
  right: 5px;
  bottom: 5px;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: var(--cfg-warning-alt);
  animation: spin 3s linear infinite; /* Chrome, Firefox 16+, IE 10+, Opera */
}

#loader:after {
  content: "";
  position: absolute;
  top: 15px;
  left: 15px;
  right: 15px;
  bottom: 15px;
  border-radius: 50%;
  border: 3px solid transparent;
  border-top-color: var(--cfg-warning);
  animation: spin 1.5s linear infinite; /* Chrome, Firefox 16+, IE 10+, Opera */
}

.configurator-options__wrapper {
  display: flex;
  position: relative;
  align-items: stretch;
}

.configurator-option__list-image {
  display: inherit;
  min-width: 170px;
  margin: 0 auto;
  width: 100%;
  max-width: 200px;
}

.configurator-options__image {
  position: relative;
  // Fix for Firefox: SVG elements should have an absolute width set
  max-width:100px;
  height: 100px;
  object-fit: contain;
}

@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  /* IE10+ CSS styles go here */
  .configurator-options__image {
    width: 100px;
  }
}

.configurator-options__image-holder {
  max-height: 100px;
  margin-top: 11px;
}

.configurator-options__item {
  display: flex;
  position: relative;
  border-radius: 8px;
  padding: 56px 26px 0 26px;
  gap: 8px;
  border: 1px solid var(--cfg-primary-faint);

  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1;

  &:hover {
    background-color: var(--cfg-primary-tint-alt);
  }

  &--active {
    border-color: var(--cfg-primary);
    box-shadow: none;
    background: var(--cfg-primary-tint-alt);

    .orientation {
      border-color: var(--cfg-primary);
    }
  }

  &--deferred .configurator-block__label {
    font-weight: 400;
    color: #555;
  }

  &--list {
    margin-bottom: 5px;
    display: flex;
    align-items: center;
  }

  &--unmet-requirements {
    .configurator-block__label,
    .configurator-options__shape {
      opacity: 0.3;
      cursor: default;
      pointer-events: none;
    }
  }

  &--light .configurator-options__shape {
    background: white;
    border-color: #e4e4e4;
  }
}

.best-choice--text {
  color: var(--cfg-error);
  font-size: 1rem;
  text-transform: uppercase;
  font-weight: 900;
  display: block;
}

.best-choice--label {
  padding: 3px 6px;
  color: white;
  background:var(--cfg-error-dark);
  font-size: 1.2rem;
  line-height: 1.2;
  font-weight: bold;
  display: block;
  text-align: center;
  z-index: 1;
  border-radius: 0 0 4px 4px;
}

/* Flags OnlyForTesting-tagged options/groups: only visible to internal testers
   (fotofabriek.nl / chrisrussell.nl accounts), never shown to real customers. */
.only-for-testing--label {
  padding: 3px 6px;
  color: #4d3800;
  background: var(--cfg-warning);
  font-size: 1.2rem;
  line-height: 1.2;
  font-weight: bold;
  display: block;
  text-align: center;
  z-index: 1;
  border-radius: 0 0 4px 4px;
  cursor: help;
}

.only-for-testing--tag {
  padding: 1px 6px;
  color: #4d3800;
  background: var(--cfg-warning);
  font-size: 1rem;
  line-height: 1.4;
  font-weight: bold;
  text-transform: uppercase;
  border-radius: 4px;
  margin-left: 6px;
  cursor: help;
}

.scroll-hook {
  position: absolute;
  top: -100px;
  width: 100%;
}

@-webkit-keyframes spin {
  0% {
    -webkit-transform: rotate(0deg); /* Chrome, Opera 15+, Safari 3.1+ */
    -ms-transform: rotate(0deg); /* IE 9 */
    transform: rotate(0deg); /* Firefox 16+, IE 10+, Opera */
  }
  100% {
    -webkit-transform: rotate(360deg); /* Chrome, Opera 15+, Safari 3.1+ */
    -ms-transform: rotate(360deg); /* IE 9 */
    transform: rotate(360deg); /* Firefox 16+, IE 10+, Opera */
  }
}
@keyframes spin {
  0% {
    -webkit-transform: rotate(0deg); /* Chrome, Opera 15+, Safari 3.1+ */
    -ms-transform: rotate(0deg); /* IE 9 */
    transform: rotate(0deg); /* Firefox 16+, IE 10+, Opera */
  }
  100% {
    -webkit-transform: rotate(360deg); /* Chrome, Opera 15+, Safari 3.1+ */
    -ms-transform: rotate(360deg); /* IE 9 */
    transform: rotate(360deg); /* Firefox 16+, IE 10+, Opera */
  }
}

.configurator-options__shape {
  transition: border-color 0.2s ease;
  position: relative;

  &--square,
  &--circle {
    border: 2px solid var(--cfg-primary-faint);
  }
  &--square {
    border-radius: 5px;
  }
  &--circle {
    border-radius: 50%;
  }

  &--border-white {
    border: 2px solid #fff;
  }

  &--background-blue {
    background: #d9e6ef;
  }
  &--background-white {
    background: #fff;
  }
}

.tool-tip__label {
  display: flex;
  width: 20px;
  height: 20px;
  top: 8px;
  left: 8px;
  background:var(--cfg-primary-border);
  border: 2px solid var(--cfg-primary-border);
  border-radius: 100%;

  justify-content: center;
  align-items: center;

  color: #FFF;
  font-size: 1.4rem;
  font-weight: 700;

  cursor: pointer;
  user-select: none;
  z-index: 1;

  @at-root .tool-tip__wrapper--active .tool-tip__arrow-hook:before,
    &:not(&--large):before {
      content: "";
      position: absolute;
      width: 0;
      height: 0;
      left: 50%;
      top: -10px;
      margin-left: -10px;

      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      border-top: 10px solid rgba(40, 40, 40, 0.95);
      z-index: 3;
  }

  &--large {
    border-radius: 15px;
    padding: 0 10px;
    font-size: 1.3rem;
    font-weight: 500;
    width: auto;
  }

  &--absolute {
    position: absolute;
  }

  &:hover {
    background: var(--cfg-primary);
    border-color: var(--cfg-primary);
    color: #FFF;
  }
}

.configurator-block__label {
  font-size: 1.6rem;
  line-height: 1.4;
  font-weight: 600;
  letter-spacing: .2px;
  text-align: center;
  word-break: break-word;
  height: 56px;

  &--inline {
    display: inline;
  }

  &--inner {
    display: flex;
    position: absolute;
    bottom: 0;
    right: 0;
    left: 0;
    top: auto;
    padding: 20px 0;
    background: rgba(0, 0, 0, 0.4);

    color: white;
    align-items: center;
    justify-content: center;
  }
}

.configurator-overview__table {
  line-height: 2.3rem;
  margin-top: 10px;
}

.configurator-overview__table tr > td:nth-child(2) {
  padding-left: 20px;
}

.configurator-overview__table-title {
  font-size: 1.4rem;
  font-weight: 700;
}

.configurator-overview__table-text {
  font-size: 1.4rem;
}

.configurator__input-selectable {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  border-radius: 10px;
  border: 2px solid var(--cfg-primary-border);
}

.configurator-options__item--active {
  .configurator__input-selectable {
    border-color: var(--cfg-primary);
  }

  .configurator__input-selectable::after {
    content: "";
    position: absolute;
    top:2px;
    left: 6px;
    width: 5px;
    height: 10px;
    border: 2px solid transparent;
    border-bottom-color: var(--cfg-primary);
    border-right-color: var(--cfg-primary);
    transform: rotate(45deg);
  }
}

.amount-input {
  display: flex;
  margin-left: 8px;

  &--table {
    margin: 0 auto;
  }
}

.amount-holder {
  display: flex;
}

.amount-field {
  padding: 5px;
  width: 60px;
  height: 32px;
  border-radius: 0;
  border: 1px solid var(--cfg-primary-tint);
  background: #fff;
  box-shadow: none;

  text-align: center;
  font-size: 1.7rem;
  font-weight: 600;

  @at-root select#{&} {
    width: 100px;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }

  &--invalid {
    border-color: red;
  }
}

.amount-button {
  height: 32px;
  min-width: 32px;
  max-width: 32px;
  background: var(--cfg-primary-tint);
  color: var(--cfg-primary-dark);

  font-size: 15px;
  font-weight: 900;
  transition: background 0.1s ease;
  cursor: pointer;

  &:first-child {
    border-right: none;
    border-radius: 4px 0 0 4px;
  }

  &:last-child {
    border-left: none;
    border-radius: 0 4px 4px 0;
  }

  &:hover {
    background: var(--cfg-primary-dark);
    color: var(--cfg-primary-tint);
  }
}

.configurator-step__page-selector {
  padding: 16px;
  border-radius: 8px;
  border: 1px solid var(--cfg-primary-border);
  background: var(--cfg-primary-tint-alt);
}

.configurator-step__page-selector .amount-field {
	font-family: 'Inter', serif;
	color: #1F2A37;
}

.amount-input__range {
  position: relative;
  width: 100%;
  max-width: 320px;
  height: 8px;
  border: none;
  background: #FFF;
  border-radius: 4px;

  -webkit-appearance: none;
  appearance: none;
  outline: none;
  opacity: 0.9;
  -webkit-transition: .2s;
  transition: opacity .2s;
}

.amount-input__range:hover {
  opacity: 1;
}

.amount-input__range::-ms-track {
  background: transparent; /* Vereist voor oudere Edge */
  border-color: transparent;
  height: 8px;
}

.amount-input__range::-ms-thumb {
  width: 25px;
  height: 25px;
  border-radius: 999px;
  background: #FFF;
  border: 2px solid var(--cfg-primary-dark);
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
}

.amount-input__range::-moz-range-thumb,
.amount-input__range::-webkit-slider-thumb {
  width: 25px;
  height: 25px;
  border-radius: 999px;
  background: #FFF;
  border: 2px solid var(--cfg-primary-dark);
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
}

.error-message,
.success-message {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-weight: bold;
  color: #60bd6f;
}

.error-message {
  color: var(--cfg-error-alt);
}

.checkmark__circle,
.error__circle {
  stroke-dasharray: 166;
  stroke-dashoffset: 166;
  stroke-width: 2;
  stroke-miterlimit: 10;
  fill: none;
  animation: stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
}
.checkmark__circle {
  stroke: #60bd6f;
}
.error__circle {
  stroke: var(--cfg-error-alt);
}

.checkmark,
.error {
  display: inline-block;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  stroke-width: 5;
  stroke: #fff;
  stroke-miterlimit: 10;
  margin-right: 1.5rem;
}

.checkmark {
  box-shadow: inset 0px 0px 0px #7ac142;
  animation: fill-green 0.4s ease-in-out 0.4s forwards,
    scale 0.3s ease-in-out 0.9s both;
}

.error {
  stroke: inset 0 0 0 var(--cfg-error-alt);
  animation: fill-red 0.4s ease-in-out 0.4s forwards,
    scale 0.3s ease-in-out 0.9s both;
}

.checkmark__check,
.error__cross {
  transform-origin: 50% 50%;
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  stroke: #fff;
  animation: stroke 0.4s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards;
}

.error-message__text,
.success-message__text {
  font-size: 1.7rem;
  opacity: 0;
  animation: fade 0.3s cubic-bezier(0.65, 0, 0.45, 1) 1s forwards;
}

.error-message__holder {
  display: block;
  max-width: 100%;
  margin-top: 15px;
  padding: 10px 20px;
  background: #f5f5f5;
  border-radius: 4px;
  clear: both;
  flex: 1 0 100%;
}

.orientation__svg {
  width: 100%;
  height: 100%;
  padding: 3px;
}

.orientation {
  display: flex;
  position: absolute;
  bottom: -5px;
  left: -5px;
  height: 35px;
  width: 35px;
  background: white;
  border-radius: 50px;
  border: 4px solid #ccc;

  align-items: center;
  justify-content: center;
  user-select: none;

  &--relative {
    position: relative;
    bottom: auto;
    left: auto;
    display: inline-flex;
  }

  &--indicator {
    height: 25px;
    width: 25px;
    margin: -15px 0;
    border-radius: 5px;
    border: 2px solid #ccc;

    .orientation__svg {
      padding: 2px;
    }
  }
}

.configurator-step .orientation-indicator {
  display: none;
}

.configurator-step--active .orientation-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-bottom: 10px;
}

.orientation-indicator__text {
  padding-left: 10px;
  font-size: 58%;
  font-weight: 300;
}

.indicator-container {
  position: absolute;

  &--top {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    top: 0;
    left: 0;
    padding: 0 36px;
  }

  &--bottom {
    bottom: 0;
    right: 0;
  }

  .indicator {
    width: 24px;
    height: 24px;
  }

  .indicator__icon {
    margin: 3px 3px;
    width: 18px;
    height: 18px;
  }

  .svg {
    color: #FFF;
  }

  .tool-tip__wrapper--offset {
    .indicator__icon {
      width: 14px;
      height: 14px;
      margin: 5px;
    }
  }
}

.offset-indicator__icon {
  display: inline-block;
  position: relative;
  padding: 1px 4px 4px;
}
.configurator__block {
  position: relative;
}
.svg {
  display: inline-block;
  width: 100%;
  height: 100%;
  fill: currentColor;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s;
}

.fade-enter-active + .tool-tip__label:before {
  opacity: 1;
}

.fade-leave-active + .tool-tip__label:before {
  opacity: 0;
}

.fade-enter,
.fade-leave-to
{
  opacity: 0;
}

@keyframes fade {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes stroke {
  100% {
    stroke-dashoffset: 0;
  }
}
@keyframes scale {
  0%,
  100% {
    transform: none;
  }
  50% {
    transform: scale3d(1.1, 1.1, 1);
  }
}
@keyframes fill-green {
  100% {
    box-shadow: inset 0px 0px 0px 30px #60bd6f;
  }
}
@keyframes fill-red {
  100% {
    box-shadow: inset 0px 0px 0px 30px var(--cfg-error-alt);
  }
}

hr {
  margin: 3em 0;
  background: none;
  border: 1px solid #f5f5f5;
  color: transparent;
}

@media screen and (max-width: 1000px) {
  .tool-tip__label--large {
    font-size: 1.4rem;
  }

  .orientation {
    bottom: -5px;
    left: -5px;
    border-width: 3px;
    height: 25px;
    width: 25px;

    &--relative {
      position: relative;
      bottom: auto;
      left: auto;
      display: inline-flex;
    }

    &--indicator {
      border: 2px solid #ccc;
      height: 25px;
      width: 25px;
      margin: -10px 0;
      border-radius: 5px;
    }
  }

  .orientation__svg {
    padding: 3px;
  }

}

@media screen and (max-width: 725px) {
  .configurator__holder {
    padding: 0;
    box-shadow: none;
    border-radius: 0;
    overflow: visible;
  }

  .tool-tip__label--large {
    font-size: 1.3rem;
    padding: 0 7px;
    height: 25px;
  }

  .configurator-option__list-image {
    max-width: 150px;
  }

  .configurator-options-wrapper {
    justify-content: flex-start;
  }

  small.orientation-indicator__text {
    font-size: 60%;
  }

  .configurator-options__image {
    max-width: 100px;
    height: 75px !important;
    margin: 0;
  }

}

@media screen and (max-width: 600px) {
  .price-overview-row .amount-input {
    flex-direction: column;
    align-items: stretch;

    .amount-button {
      max-width: 100%;

      &:first-child {
        border-radius: 4px 4px 0 0;
      }
      &:last-child {
        border-radius: 0 0 4px 4px;
      }
    }
  }
}

@media screen and (max-width: 480px) {
  .loading--smaller {
    display: flex;
    align-items: center;
    margin: 0 !important;
  }

  .configurator-price-holder-wrapper {
    display: flex;
    align-items: center;
    height: 53px;
  }

  .configurator-price-holder__side--right {
    justify-content: start !important;
  }

  .orientation-indicator {
    display: none !important;
  }

  .configurator-options__item {
    display: grid;
    grid-template-rows: min-content min-content;
    align-items: start;
    gap: 4px;
    padding: 40px 16px 0;
  }

  .configurator-options__image,
  .configurator-options__additional-icon {
    margin: 0 auto;
  }

  .configurator-block__label {
    height: unset;
    font-size: 1.4rem;
    line-height: 1.2;
    margin-bottom: 8px;
  }
}
</style>



// WEBPACK FOOTER //
// src/App.vue